import React, { useState, useEffect, useRef } from 'react';
import { OutcomesContext } from '../../pages/outcomes/OutcomesContext';
import { OutcomeEvaluation, OutcomesQuestion } from '../../utils/Definitions';
import { OutcomesQuestionNullSwitch } from './OutcomesQuestionNullSwitch';

interface Props {
    OutcomeEvaluation: OutcomeEvaluation | null,
    OutcomesQuestion: OutcomesQuestion | null;
    setOutcomeEvaluationObject: React.Dispatch<React.SetStateAction<OutcomeEvaluation | null>>;
    onChange: any;
    ChildOnChange: any;
    GetText: any;
    NullQuestion: any;
    SetValidations: any;
    GetValidation: any;
    GetNullValue: any;
}

export const OutcomesQuestionTypeVineland: React.FC<Props> = (props) => {
    const [part1, setPart1] = useState<string>("");
    const [part2, setPart2] = useState<string>("");

    // derive text once each render (stable primitive)
    const text = props.GetText(props.OutcomesQuestion) || "";

    // ref to track last value we told parent about � prevents repeated pushes
    const lastSentRef = useRef<string | null>(null);

    // keep local state in sync with incoming text (only update when different)
    useEffect(() => {
        const [p1, p2] = text.split(":");
        const newP1 = (p1 ?? "").toString();
        const newP2 = (p2 ?? "").toString();

        if (part1 !== newP1) setPart1(newP1);
        if (part2 !== newP2) setPart2(newP2);
        // note: no extra deps, this runs when `text` changes
    }, [text]); // intentionally depends on text only

    function Validate(): void {
        const combined = `${part1}:${part2}`;
        if (combined.trim().length === 0) {
            props.SetValidations(props.OutcomesQuestion, true);
            return;
        }
        // This regex matches strings like: <12:34+, 12:34, 12, <12, etc.
        const pattern: RegExp = /^(<)?(\d+)(?::(\d+))?\+?$/;
        const isMatch: boolean = pattern.test(combined);
        props.SetValidations(props.OutcomesQuestion, isMatch);
    }

    function IsDisabled(): boolean {
        const nullable = props.OutcomeEvaluation?.nullables.find(
            x => x.questionID === props.OutcomesQuestion?.question.questionId
        );
        return nullable ? nullable.value === "True" : false;
    }

    // Only notify parent when the *real* combined value differs from what the parent currently has
    // or differs from the last value we already sent.
    useEffect(() => {
        // normalize combined: treat empty parts as null (no answer)
        const p1Trim = part1 ? part1.toString().trim() : "";
        const p2Trim = part2 ? part2.toString().trim() : "";

        const combinedNormalized = (p1Trim === "" && p2Trim === "") ? null : `${p1Trim}:${p2Trim}`;

        // use the `text` value we derived above as the parent's current value (normalize empty -> null)
        const currentTextNormalized = (text && text.toString().trim() !== "") ? text.toString() : null;

        const lastSent = lastSentRef.current;

        // If currentText already equals combined, no need to push.
        // If we already sent this value previously, also skip.
        if (combinedNormalized !== currentTextNormalized && combinedNormalized !== lastSent) {
            // push to parent
            props.ChildOnChange(combinedNormalized, props.OutcomesQuestion);
            lastSentRef.current = combinedNormalized;
        }
        // dependencies: only when local parts or incoming text change
    }, [part1, part2, text, props.ChildOnChange, props.OutcomesQuestion]);

    return (
        <div
            key={props.OutcomesQuestion?.question.questionId}
            className="w-full xl:w-[38rem] h-fit bg-gray-50 border drop-shadow rounded mt-4 p-2"
        >
            <span className="text-xl font-bold">
                {props.OutcomesQuestion?.question.questionText}
            </span>

            <div className="w-full h-fit m-auto flex">
                {OutcomesQuestionNullSwitch({
                    onChange: props.onChange,
                    OutcomeEvaluation: props.OutcomeEvaluation,
                    OutcomesQuestion: props.OutcomesQuestion,
                    setOutcomeEvaluationObject: props.setOutcomeEvaluationObject,
                    NullQuestion: props.NullQuestion,
                    GetNullValue: props.GetNullValue,
                })}

                <div className="grow flex items-center gap-2">
                    <input
                        onBlur={Validate}
                        disabled={IsDisabled()}
                        onChange={(e) => {
                            const newValue = e.target.value;
                            setPart1(prev => (prev !== newValue ? newValue : prev));
                        }}
                        value={part1}
                        autoComplete="off"
                        type="text"
                        className={
                            "outline-none h-[38px] focus:border-2 border-gray-300 text-gray-900 rounded block w-full p-2.5 " +
                            (!IsDisabled() && props.GetValidation(props.OutcomesQuestion)
                                ? "border-gray-300 border focus:border-lacblue focus:ring-lacblue"
                                : !IsDisabled()
                                    ? "border-rose-300 border-2 focus:ring-rose"
                                    : "border-gray-300")
                        }
                    />

                    <span>:</span>

                    <input
                        onBlur={Validate}
                        disabled={IsDisabled()}
                        onChange={(e) => {
                            const newValue = e.target.value;
                            setPart2(prev => (prev !== newValue ? newValue : prev));
                        }}
                        value={part2}
                        autoComplete="off"
                        type="text"
                        className={
                            "outline-none h-[38px] focus:border-2 border-gray-300 text-gray-900 rounded block w-full p-2.5 " +
                            (!IsDisabled() && props.GetValidation(props.OutcomesQuestion)
                                ? "border-gray-300 border focus:border-lacblue focus:ring-lacblue"
                                : !IsDisabled()
                                    ? "border-rose-300 border-2 focus:ring-rose"
                                    : "border-gray-300")
                        }
                    />
                </div>
            </div>
        </div>
    );
};
